/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { MemberRoleType, UserRoleType } from '@/types/enumTypes';
import { AUTH } from '@/utils/constants';

export interface AuthState {
  Mode: string;
  authState: boolean;
  emailAddress: string;
  loggedInName: string;
  token: string;
  googleAuthId: string;
  appleAuthId: string;
  appleAccesToken: string;
  facebookAuthId: string;
  isCoBrowse: boolean;
  isCoEdit: boolean;
  RefreshToken: string;
  RefreshTokenId: string;
  isTaxProfessional: boolean;
  locale: string;
  returnIds: string[];
  roleId: UserRoleType | null;
  mainUserName: string;
  userAccountCount: number;
  isDirty: boolean;
  isTwoStepVerification: boolean;
  memberRole: MemberRoleType | null;
  isWorkFlowSettingEnabled: boolean;
  isEmailVerified: boolean;
}

const initialState: AuthState = {
  Mode: 'light',
  authState: false,
  emailAddress: '',
  loggedInName: '',
  token: '',
  googleAuthId: '',
  appleAuthId: '',
  appleAccesToken: '',
  facebookAuthId: '',
  isCoBrowse: false,
  isCoEdit: false,
  RefreshToken: '',
  RefreshTokenId: '',
  isTaxProfessional: false,
  locale: 'en',
  returnIds: [],
  roleId: null,
  mainUserName: '',
  userAccountCount: 0,
  isDirty: false,
  isTwoStepVerification: false,
  memberRole: null,
  isWorkFlowSettingEnabled: false,
  isEmailVerified: true,
};

export const authSlice: any = createSlice({
  name: AUTH,
  initialState,
  reducers: {
    setMode: (state, action) => {
      state.Mode = action.payload;
    },
    setAuthState: (state, action) => {
      state.authState = action.payload;
    },
    setEmailAddress: (state, action) => {
      state.emailAddress = action.payload;
    },
    setLoggedInName: (state, action) => {
      state.loggedInName = action.payload;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setGoogleAuthId: (state, action) => {
      state.googleAuthId = action.payload;
    },
    setAppleAuthId: (state, action) => {
      state.appleAuthId = action.payload;
    },
    setAppleAccesToken: (state, action) => {
      state.appleAccesToken = action.payload;
    },
    setFacebookAuthId: (state, action) => {
      state.facebookAuthId = action.payload;
    },
    setIsCoBrowser: (state, action) => {
      state.isCoBrowse = action.payload;
    },
    setIsCoEdit: (state, action) => {
      state.isCoEdit = action.payload;
    },
    setRefreshToken: (state, action) => {
      state.RefreshToken = action.payload;
    },
    setRefreshTokenId: (state, action) => {
      state.RefreshTokenId = action.payload;
    },
    setIsTaxProfessional: (state, action) => {
      state.isTaxProfessional = action.payload;
    },
    setLocale(state, { payload }: PayloadAction<string>) {
      state.locale = payload;
    },
    setReturnIds(state, { payload }: PayloadAction<string[]>) {
      state.returnIds = payload;
    },
    setRoleId: (state, action) => {
      state.roleId = action.payload;
    },
    setMainUserName: (state, action) => {
      state.mainUserName = action.payload;
    },
    setUserAccountCount: (state, action) => {
      state.userAccountCount = action.payload;
    },
    setIsDirty: (state, action) => {
      state.isDirty = action.payload;
    },
    setIsTwoStepVerification: (state, action) => {
      state.isTwoStepVerification = action.payload;
    },
    setMemberRole: (state, action) => {
      state.memberRole = action.payload;
    },
    setIsWorkFlowSettingEnabled: (state, action) => {
      state.isWorkFlowSettingEnabled = action.payload;
    },
    setIsEmailVerified: (state, action) => {
      state.isEmailVerified = action.payload;
    },
  },
});

export const {
  setMode,
  setAuthState,
  setEmailAddress,
  setLoggedInName,
  setToken,
  setGoogleAuthId,
  setAppleAuthId,
  setAppleAccesToken,
  setFacebookAuthId,
  setIsCoBrowser,
  setIsCoEdit,
  setRefreshToken,
  setIsTaxProfessional,
  setLocale,
  setReturnIds,
  setIsDirty,
  setRoleId,
  setMainUserName,
  setUserAccountCount,
  setIsTwoStepVerification,
  setMemberRole,
  setIsWorkFlowSettingEnabled,
  setIsEmailVerified,
  setRefreshTokenId,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
